/** @jsx jsx */
import { useSkeem } from "../providers/SkeemProvider"
import { P } from "@cd2/text/build/es6"
import { useTheme } from "@cd2/theme/build/es6"
import { css, jsx } from "@emotion/core"
import Tippy from "@tippyjs/react"
import { Instance } from "tippy.js"
import { differenceInSeconds, endOfToday, formatDistanceToNowStrict, startOfToday } from "date-fns"
import { useEffect, useRef } from "react"
import { IRecord } from "../types/schema"
import { IconButton } from "./IconButton"
import { quickTooltipContentCss } from "./QuickTooltip"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { icons } from "../utils/icons"
import { LoadingFrame } from "../pages/user/UserResultsShowPage/AssessmentDetailsTab"
import { useReloadKey } from "../hooks/useReloadKey"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { TextWithNewLines } from "./TextWithNewLines"
import { useStateWithGetter } from "../hooks/useStateWithGetter"

type Notification = IRecord<"notifications", "heading" | "body" | "route" | "createdAt">
type Reminder = IRecord<"reminders", "body" | "date" | "completedOn" | "archived">

type Message = {
  type: "notification" | "reminder"
  id: string
  heading: string
  body: string
  route: string
  date: Date | null
}

declare global {
  interface Window {
    NotificationBell: {
      triggerIfPending(): void
      triggerIfPendingAndNotTriggeredInAWhile(): void
    }
  }
}

const NOTIFICATION_POLL_RATE = 1000 * 60

export function NotificationsBell() {
  const skeem = useSkeem()
  const theme = useTheme()

  const tippyRef = useRef<Instance | undefined>(undefined)
  const [reloadKey, triggerReload] = useReloadKey()
  const [messages, setMessages, getMessages] = useStateWithGetter<Message[] | null>(null)

  useAsyncEffect(
    async ({ isAlive }) => {
      const notifications = await skeem.fetch2<Notification[]>({
        model: {
          name: "notifications",
          attributes: ["heading", "body", "route", "createdAt"],
          filter: { matches: { user: { session: true }, unread: true } },
        },
      })

      const reminders = await skeem.fetch2<Reminder[]>({
        model: {
          name: "reminders",
          attributes: ["body", "date", "completedOn", "archived"],
          filter: {
            and: [
              { matches: { user: { session: true }, archived: false, completedOn: null } },
              { matches: { date: { gte: startOfToday() } } },
              { matches: { date: { lte: endOfToday() } } },
            ],
          },
        },
      })

      setMessages(prevMessages => {
        // Check if there are any new messages and show the tooltip
        if (prevMessages) {
          const knownMessageIds = prevMessages.map(m => m.id) || []
          const hasNewNotifications = notifications.some(n => !knownMessageIds.includes(n.id))
          const hasNewReminders = reminders.some(r => !knownMessageIds.includes(r.id))
          if (hasNewNotifications || hasNewReminders) {
            tippyRef.current?.show()
          }
        }

        const newMessages = [
          ...notifications.map(n => ({
            type: "notification" as const,
            id: n.id,
            heading: n.heading,
            body: n.body,
            route: n.route,
            date: new Date(n.createdAt!),
          })),
          ...reminders.map(r => ({
            type: "reminder" as const,
            id: r.id,
            heading: "A task is due today",
            body: r.body,
            route: "/reminders",
            date: r.date ? new Date(r.date) : null,
          })),
        ].sort((a, b) => (b.date && a.date ? b.date.getTime() - a.date.getTime() : 0))

        return newMessages
      })

      setTimeout(() => {
        if (isAlive()) triggerReload()
      }, NOTIFICATION_POLL_RATE)
    },
    [reloadKey],
  )

  useEffect(() => {
    window.NotificationBell = {
      triggerIfPending() {
        const messages = getMessages()
        if (messages && messages.length > 0) {
          tippyRef.current?.show()
        }
      },
      triggerIfPendingAndNotTriggeredInAWhile() {
        const lastTriggerTime = localStorage.getItem("lastTriggerTime")
        if (!lastTriggerTime || differenceInSeconds(new Date(), new Date(lastTriggerTime)) > 60) {
          window.NotificationBell.triggerIfPending()
          localStorage.setItem("lastTriggerTime", new Date().toISOString())
        }
      },
    }
  }, [])

  const handleMarkRead = useAsyncCallback(async (message: Message) => {
    if (message.type === "notification") {
      await skeem.mutate({ notifications: { update: { id: message.id, unread: false } } })
    }
    setMessages(messages => (!messages ? null : messages.filter(m => m.id !== message.id)))
  })

  return (
    <Tippy
      onCreate={instance => {
        tippyRef.current = instance
      }}
      animation="fade"
      zIndex={201} // rota is 200
      appendTo={document.body}
      onShow={triggerReload}
      maxWidth={400}
      content={
        <div
          css={css`
            max-height: 400px;
            overflow: auto;
            width: 400px;
            max-width: calc(100vw - 8px);
            padding: 8px;
          `}
        >
          {!messages && <LoadingFrame />}
          {messages && messages?.length === 0 && (
            <div
              css={css`
                min-width: 100%;
                padding: 16px 8px;
                cursor: pointer;
                background-color: white;
                text-align: center;
              `}
            >
              <P>You have no unread notifications.</P>
            </div>
          )}
          {messages?.map(message => (
            <div
              key={message.id}
              css={css`
                padding: 8px 16px;
                transition: 0.1s;
                display: flex;
                align-items: center;
                gap: 4px;
                border-left: 4px solid transparent;
                :hover {
                  border-left: 4px solid ${theme.themeColor("primary")};
                }
                b {
                  font-size: 1.1em;
                }
                p {
                  margin: 4px 0;
                }
                .date {
                  color: #999;
                  font-size: 0.8em;
                }

                .body {
                  position: relative;
                  border-radius: 5px;
                  max-height: 100px;
                  overflow: clip;
                }

                /* Gradient overlay */
                .body::after {
                  content: "";
                  position: absolute;
                  left: 0;
                  width: 100%;
                  height: 50px;
                  background: linear-gradient(to top, rgb(255 255 255) 10%, transparent);
                  pointer-events: none;
                  top: 50px;
                }
              `}
            >
              <div
                css={css`
                  flex: 1;
                `}
              >
                {message.heading && <b>{message.heading}</b>}
                <div className="body">
                  <TextWithNewLines text={message.body} />
                </div>
                {message.date && (
                  <div className="date">
                    {differenceInSeconds(new Date(), new Date(message.date)) < 30
                      ? "Just now"
                      : formatDistanceToNowStrict(new Date(message.date), {
                          addSuffix: true,
                        })}
                  </div>
                )}
              </div>
              {message.route && (
                <div>
                  <a href={message.route}>
                    <IconButton
                      icon={icons.link}
                      buttonStyle="text"
                      color={theme.colors.primary}
                      square
                    />
                  </a>
                </div>
              )}
              {message.type === "notification" && (
                <IconButton
                  onClick={() => handleMarkRead(message)}
                  icon={icons.check}
                  buttonStyle="text"
                  color={theme.colors.primary}
                  square
                />
              )}
            </div>
          ))}
        </div>
      }
      css={css(quickTooltipContentCss({ padding: 0, isLight: true }))}
      placement="bottom-end"
      trigger="click"
      interactive
    >
      <div
        css={css`
          position: relative;
          transition: 0.2s;
          border-radius: 4px;
          :hover {
            background: rgba(0, 0, 0, 0.1);
          }
        `}
      >
        {messages && messages.length > 0 && (
          <div
            css={css`
              position: absolute;
              right: 0px;
              top: 0;
              background-color: red;
              border-radius: 100%;
              width: 18px;
              height: 18px;
              border: 1px solid white;
              display: flex;
              justify-content: center;
              align-items: center;
              color: white;
              font-size: ${messages.length > 9 ? "10px" : "12px"};
              pointer-events: none;
              user-select: none;
            `}
          >
            {messages.length > 9 ? "+9" : messages.length}
          </div>
        )}
        <IconButton
          icon="bell"
          prefix={messages && messages.length > 0 ? "solid" : "regular"}
          buttonStyle="text"
          color="white"
          iconSize={20}
          square
        />
      </div>
    </Tippy>
  )
}
